import { queryOptions } from '@tanstack/react-query';
import type { usePermissions } from '@customerio/hydra-permissions/react';
import { AccountFeatureFlag } from '@/services/auth/feature-flags';
import { hasUnlimitedPlanFeatureAccess } from '@/services/auth/plan-features';
import { BUILDER_PLAN_SLUG } from '@/services/auth/plan-slugs';
import { PlanFeature, type AugmentedAccount, type SearchResult } from '@/services/auth/types';
import { defaultFilter } from 'cmdk';
import { createProductTaxonomy, type ProductTaxonomy } from '@/services/product-taxonomy';
import { getNavigationLabels } from './navigation-manifest';

export type PermissionGuard = Pick<ReturnType<typeof usePermissions>, 'hasPermission' | 'hasAnyPermission'> & {
  account: AugmentedAccount | null;
  hasFeatureFlag: (flag: AccountFeatureFlag) => boolean;
};

type GuardFunction = (guard: PermissionGuard) => boolean;

// Routes that redirect builder plan users to the billing upgrade page. Renamed segments list
// both the legacy and renamed paths so the guard matches whichever is canonical for the account
// (Analysis → Reports, Campaigns → Automations).
const BUILDER_PLAN_GATED_ROUTE_PREFIXES = [
  '/journeys/analysis',
  '/journeys/reports',
  '/journeys/campaigns',
  '/journeys/automations',
  '/journeys/message-library',
  '/journeys/goals',
  '/journeys/objects',
  '/workspaces/last/objects',
  '/workspaces/$workspaceId/objects',
  '/journeys/integrations',
  // The migrated reporting-webhooks and warehouse URLs drop the `journeys` segment, so they no
  // longer match the entry above and need their own.
  '/integrations/reporting-webhooks',
  '/integrations/warehouse',
  '/journeys/layouts',
  '/journeys/collections',
  '/journeys/settings/objects',
];

const ROUTE_GUARDS = [
  {
    // Both taxonomy segments: `/analysis/...` pre-rename, `/reports/...` once the flag is on.
    test: (r) => /\/(analysis|reports)\/(deliverability|email-clients)/.test(r),
    guard: ({ account, hasFeatureFlag }) =>
      account?.plan_slug !== BUILDER_PLAN_SLUG && hasFeatureFlag(AccountFeatureFlag.DeliverabilityAnalytics),
  },
  {
    test: (r) =>
      BUILDER_PLAN_GATED_ROUTE_PREFIXES.some((prefix) => r.includes(prefix)) ||
      /\/workspaces\/(last|\d+)\/objects(?:\/|$)/.test(r),
    guard: ({ account }) => account?.plan_slug !== BUILDER_PLAN_SLUG,
  },
  {
    // Goals (FLO-4772). Flag off, `/journeys/goals` renders Hydra's NotFoundPage, so the
    // palette must not offer it — picking the result would land on a 404.
    test: (r) => r.includes('/journeys/goals'),
    guard: ({ hasFeatureFlag }) => hasFeatureFlag(AccountFeatureFlag.ShowGoals),
  },
  {
    test: (r) => r.includes('/journeys/anonymous'),
    guard: ({ account }) => account?.planFeatures[PlanFeature.AnonymousInApp].enabled ?? false,
  },
  {
    test: (r) => /\/workspaces\/(last|\d+)\/(settings\/geofences|(journeys\/)?geofences)(\/|$)/.test(r),
    // Match the geofences routes' own guard: they redirect users lacking
    // journeys/geofences/view, so don't offer them as search results.
    guard: ({ account, hasPermission }) =>
      account != null &&
      account.planFeatures != null &&
      hasUnlimitedPlanFeatureAccess(account, PlanFeature.Geofences) &&
      hasPermission('journeys/geofences/view'),
  },
  {
    // Both the Ember URL and the migrated one, which drops the `journeys` segment.
    test: (r) => r.includes('/journeys/integrations') || r.includes('/integrations/reporting-webhooks'),
    guard: ({ hasAnyPermission }) => hasAnyPermission('journeys/imports/view', 'journeys/exports/view'),
  },
  {
    // The migrated warehouse pages, which drop the `journeys` segment. Narrower than the
    // integrations clause above on purpose: `WarehouseRouteFrame` wraps them in
    // `ProtectedRoute permission="journeys/imports/view"`, so exports-only users would be
    // redirected to the workspace home right after picking the result.
    test: (r) => r.includes('/integrations/warehouse'),
    guard: ({ hasPermission }) => hasPermission('journeys/imports/view'),
  },
  {
    // Both spellings, since the imports migration drops the `journeys` segment. Anchored on the
    // workspace segment rather than matched loosely, so `journeys/sql-imports` is left to its own
    // clause; the same reason the warehouse clause above is narrower than the integrations one.
    test: (r) => /\/workspaces\/(last|\d+)\/(journeys\/)?imports(\/|\?|$)/.test(r),
    guard: ({ hasPermission }) => hasPermission('journeys/imports/view'),
  },
  {
    test: (r) => r.includes('/journeys/exports'),
    guard: ({ hasPermission }) => hasPermission('journeys/exports/view'),
  },
  {
    test: (r) => r.includes('/design-studio/'),
    guard: ({ hasPermission }) => hasPermission('parcel/emails/view') && hasPermission('parcel/components/view'),
  },
  {
    test: (r) => r.includes('/agent'),
    guard: ({ account }) => account?.public_settings?.ai_enabled === true,
  },
  {
    test: (r) => /\/workspaces\/(last|\d+)\/settings(\/|$)/.test(r),
    guard: ({ hasPermission }) => hasPermission('journeys/workspace_settings/view'),
  },
  {
    test: (r) => /\/workspaces\/(last|\d+)\/journeys\/settings(\/|$)/.test(r),
    guard: ({ hasPermission }) => hasPermission('journeys/workspace_settings/view'),
  },
  {
    test: (r) =>
      r.includes('/journeys/settings/api_credentials') ||
      r.includes('/settings/api_credentials') ||
      r.includes('/settings/api-keys'),
    guard: ({ hasPermission }) => hasPermission('account/api_credentials/view'),
  },
  {
    test: (r) =>
      r.includes('/journeys/settings/webhook_configuration') || r.includes('/settings/webhook_configuration'),
    guard: ({ hasPermission }) => hasPermission('journeys/configuration_manager/view'),
  },
  {
    test: (r) => r.includes('/settings/ai-business-profile'),
    guard: ({ account }) => account?.public_settings?.ai_enabled === true,
  },
  {
    test: (r) => r.includes('/settings/actions/email/account_verification'),
    guard: ({ hasFeatureFlag }) => hasFeatureFlag(AccountFeatureFlag.AccountVerification),
  },
  {
    test: (r) => r === '/settings/ai',
    guard: ({ hasPermission }) => hasPermission('account/support_access/view'),
  },
  {
    test: (r) => r.includes('/settings/llm_configuration'),
    guard: ({ hasFeatureFlag, hasPermission }) =>
      hasFeatureFlag(AccountFeatureFlag.LLMActionBYOK) && hasPermission('journeys/configuration_manager/view'),
  },
  {
    // Mirrors WorkspaceConsentRoute: flag off redirects to the settings landing, so the
    // palette must not offer the page to unflagged accounts.
    test: (r) => /\/workspaces\/(last|\d+)\/settings\/consent(\/|$)/.test(r),
    guard: ({ hasFeatureFlag, hasPermission }) =>
      hasFeatureFlag(AccountFeatureFlag.ConsentRecords) && hasPermission('journeys/workspace_settings/view'),
  },
  {
    test: (r) =>
      r.startsWith('/settings/billing') || r.startsWith('/settings/subscription') || r.startsWith('/settings/receipts'),
    guard: ({ hasPermission }) => hasPermission('account/billing/view'),
  },
  {
    test: (r) => r.startsWith('/settings/audit-logs'),
    guard: ({ hasPermission }) => hasPermission('account/view'),
  },
  {
    test: (r) => r.startsWith('/settings/security') || r.startsWith('/settings/account'),
    guard: ({ hasPermission }) => hasPermission('account/view'),
  },
  {
    test: (r) => r.startsWith('/settings/roles'),
    guard: ({ hasPermission }) => hasPermission('account/team_members/view'),
  },
  {
    test: (r) => r.startsWith('/settings/api'),
    guard: ({ hasPermission }) => hasPermission('account/api_credentials/view'),
  },
  {
    test: (r) => r.includes('/settings/billing/inbox-preview-usage'),
    guard: ({ hasFeatureFlag }) => hasFeatureFlag(AccountFeatureFlag.DsInboxPreviews),
  },
  {
    // Overview, Usage and Billing details are the tabs the `billing-ui-2026` flag adds. With the
    // flag off each URL redirects away (BillingUI2026Route), so the palette hides them to match the
    // tab nav.
    test: (r) =>
      r.includes('/settings/billing/overview') ||
      r.includes('/settings/billing/usage') ||
      r.includes('/settings/billing/billing-details'),
    guard: ({ hasFeatureFlag }) => hasFeatureFlag(AccountFeatureFlag.BillingUI2026),
  },
  {
    // Payment methods is the tab Billing details replaces, so the flag hides it in the tab nav and
    // the palette follows. Searching "payment" then returns nothing rather than redirecting: cmdk
    // scores the label alone, and "Billing details" shares no characters with the query. Offering a
    // tab the account's own nav has retired is the worse of the two, so the entry goes.
    test: (r) => r.includes('/settings/billing/payment-methods'),
    guard: ({ hasFeatureFlag }) => !hasFeatureFlag(AccountFeatureFlag.BillingUI2026),
  },
  {
    // AI usage and Profile growth are the tabs the Usage tab replaces. Their pages still work by
    // URL while the flag lives, but the tab nav hides them when the flag is on, so the palette does
    // too: a flagged account searching "AI usage" reaches the Usage tab, not the retired page.
    test: (r) => r.includes('/settings/billing/llm-usage') || r.includes('/settings/billing/profile-growth'),
    guard: ({ hasFeatureFlag }) => !hasFeatureFlag(AccountFeatureFlag.BillingUI2026),
  },
  {
    // Profile growth also has its own flag: the tab shows only when `profile_growth` is on
    // (billing-tab-nav.tsx), and ProfileGrowthRoute redirects to Your plan when it is off. Without
    // this the palette would offer a dead entry when both flags are off.
    test: (r) => r.includes('/settings/billing/profile-growth'),
    guard: ({ hasFeatureFlag }) => hasFeatureFlag(AccountFeatureFlag.ProfileGrowth),
  },
] satisfies { test: (url: string) => boolean; guard: GuardFunction }[];

function isAllowed(url: string, guard: PermissionGuard): boolean {
  for (const { test, guard: guardFunction } of ROUTE_GUARDS) {
    if (test(url) && !guardFunction(guard)) return false;
  }
  return true;
}

// The manifest is the source of truth for navigable routes. getNavigationLabels resolves
// taxonomy-gated routes (renamed per-account) for the active variant. Deduplicate by label
// so aliased paths (e.g. two routes sharing a "Workspace settings" label) don't produce
// duplicate results.
function getNavigableRoutes(taxonomy: ProductTaxonomy): ReadonlyArray<{ label: string; routeTemplate: string }> {
  return [
    ...Object.entries(getNavigationLabels(taxonomy))
      .reduce((map, [routeTemplate, label]) => {
        if (!map.has(label)) map.set(label, { label, routeTemplate });
        return map;
      }, new Map<string, { label: string; routeTemplate: string }>())
      .values(),
  ];
}

const MAX_RESULTS = 10;

export function searchNavigation({
  query,
  workspaceId,
  guard,
  taxonomy = createProductTaxonomy('current'),
}: {
  query: string;
  workspaceId: number;
  guard: PermissionGuard | null;
  taxonomy?: ProductTaxonomy;
}): Extract<SearchResult, { kind: 'navigation' }>[] {
  const q = query.trim().toLowerCase();
  if (q.length < 2 || workspaceId === 0) return [];

  const matches: Extract<SearchResult, { kind: 'navigation' }>[] = [];
  for (const { label, routeTemplate } of getNavigableRoutes(taxonomy)) {
    if (defaultFilter(label, q) === 0) continue;
    const url = routeTemplate
      // Child-app routes use the manual ROUTES param convention.
      .replace(':workspaceId', String(workspaceId))
      // Hydra-owned routes use TanStack Router's generated param convention.
      .replace('$workspaceId', String(workspaceId))
      // TanStack index routes include a trailing slash; query URLs should not become `/path/?key=value`.
      .replace('/?', '?');
    if (guard != null && !isAllowed(url, guard)) continue;
    matches.push({ kind: 'navigation', title: label, url, doc_id: url });
  }

  // Prefer labels that start with the query over fuzzy mid-string matches
  matches.sort((a, b) => {
    const aStarts = a.title.toLowerCase().startsWith(q);
    const bStarts = b.title.toLowerCase().startsWith(q);
    if (aStarts && !bStarts) return -1;
    if (bStarts && !aStarts) return 1;
    return 0;
  });

  return matches.slice(0, MAX_RESULTS);
}

export function navigationSearchOptions(
  query: string,
  workspaceId: number,
  guard: PermissionGuard | null,
  { taxonomy }: { taxonomy?: ProductTaxonomy } = {},
) {
  return queryOptions({
    // guard and taxonomy are intentionally excluded from the key — permission and per-account
    // flag changes mid-session are rare and typically accompanied by a full page reload that
    // clears the cache anyway.
    queryKey: ['navigationSearch', query, workspaceId] as const,
    queryFn: async () => {
      // Artificial delay so instant local results don't feel broken to users expecting processing time
      await new Promise<void>((resolve) => setTimeout(resolve, 100));
      return searchNavigation({ query, workspaceId, guard, taxonomy });
    },
    staleTime: Infinity,
  });
}
